/** Settings → SSO. Providers + SCIM. */
import { useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, FlaskConical, RotateCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ComingOnline, SecretReveal } from "./shared";
import {
  useSsoProviders, useScimStatus, useCreateSsoProvider, useDeleteSsoProvider,
  useTestSsoProvider, useEnableScim, useRotateScimToken,
  type SsoProvider,
} from "@/hooks/security/useEdrAdmin";

const PRESETS = [
  { label: "Okta OIDC", kind: "oidc" as const },
  { label: "Microsoft Entra ID OIDC", kind: "oidc" as const },
  { label: "Google Workspace OIDC", kind: "oidc" as const },
  { label: "Generic OIDC", kind: "oidc" as const },
  { label: "Generic SAML 2.0", kind: "saml" as const },
];

export function SsoSection() {
  const providersQ = useSsoProviders();
  const scimQ = useScimStatus();
  const [addOpen, setAddOpen] = useState<{ kind: "oidc" | "saml"; label: string } | null>(null);

  const provUnavail = providersQ.data?.kind === "unavailable";
  const providers = providersQ.data?.kind === "ok" ? providersQ.data.data.providers : [];
  const scimUnavail = scimQ.data?.kind === "unavailable";
  const scim = scimQ.data?.kind === "ok" ? scimQ.data.data : null;

  return (
    <div className="space-y-4">
      <Panel title={`Single sign-on (${providers.length})`} loading={providersQ.isLoading}>
        {provUnavail ? <ComingOnline /> : (
          <div className="space-y-3">
            {providers.length === 0 && (
              <p className="text-xs text-muted-foreground py-2">No providers configured.</p>
            )}
            {providers.map((p) => <ProviderRow key={p.id} provider={p} />)}
            <div className="pt-3 border-t border-border">
              <Label className="text-xs text-muted-foreground">Off-the-shelf</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <Button key={p.label} size="sm" variant="outline" onClick={() => setAddOpen(p)}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> {p.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="SCIM user provisioning" loading={scimQ.isLoading}>
        {scimUnavail || !scim ? <ComingOnline /> : <ScimCard scim={scim} />}
      </Panel>

      <AddProviderDialog preset={addOpen} onClose={() => setAddOpen(null)} />
    </div>
  );
}

function ProviderRow({ provider }: { provider: SsoProvider }) {
  const del = useDeleteSsoProvider();
  const test = useTestSsoProvider();
  return (
    <div className="rounded border border-border p-3 space-y-1.5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-foreground">{provider.name}
            <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">{provider.kind}</span>
          </div>
          {provider.issuer && <div className="text-[11px] text-muted-foreground font-mono">{provider.issuer}</div>}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" disabled={test.isPending} onClick={() => test.mutate(provider.id)}>
            <FlaskConical className="h-3.5 w-3.5 mr-1" /> Test
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { if (confirm(`Remove ${provider.name}?`)) del.mutate(provider.id); }}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground flex flex-wrap gap-4">
        {provider.clientId && <span>Client ID: <code className="font-mono">{provider.clientId}</code></span>}
        {provider.groupClaim && <span>Group claim: <code className="font-mono">{provider.groupClaim}</code></span>}
        {provider.lastLoginAt && <span>Last login {formatDistanceToNow(new Date(provider.lastLoginAt), { addSuffix: true })}</span>}
        <span className={provider.enabled ? "text-success" : "text-muted-foreground"}>{provider.enabled ? "active" : "disabled"}</span>
      </div>
    </div>
  );
}

function ScimCard({ scim }: { scim: { enabled: boolean; endpoint?: string; lastSyncAt?: string; tokenHint?: string } }) {
  const enable = useEnableScim();
  const rotate = useRotateScimToken();
  const [rotated, setRotated] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Enabled</Label>
        <Switch checked={scim.enabled} onCheckedChange={(c) => enable.mutate(c)} disabled={enable.isPending} />
      </div>
      {scim.endpoint && (
        <div>
          <Label className="text-xs text-muted-foreground">SCIM endpoint</Label>
          <SecretReveal value={scim.endpoint} />
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-xs text-muted-foreground">Bearer token</Label>
          <Button size="sm" variant="outline" disabled={rotate.isPending} onClick={async () => {
            if (!confirm("Rotate the SCIM token? Existing integrations will break until updated.")) return;
            const r = await rotate.mutateAsync();
            setRotated(r.token);
          }}>
            <RotateCw className={`h-3.5 w-3.5 mr-1 ${rotate.isPending && "animate-spin"}`} /> Rotate
          </Button>
        </div>
        {rotated ? (
          <>
            <SecretReveal value={rotated} />
            <p className="text-[10px] text-warning mt-1">Shown once. Copy it now.</p>
          </>
        ) : (
          <code className="block px-3 py-2 rounded border border-border bg-muted/40 font-mono text-xs">
            {scim.tokenHint || "•••••••••••"}
          </code>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">Last sync: {scim.lastSyncAt ? formatDistanceToNow(new Date(scim.lastSyncAt), { addSuffix: true }) : "never"}</p>
    </div>
  );
}

function AddProviderDialog({ preset, onClose }: { preset: { kind: "oidc" | "saml"; label: string } | null; onClose: () => void }) {
  const [name, setName] = useState("");
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [groupClaim, setGroupClaim] = useState("");
  const create = useCreateSsoProvider();

  if (!preset) return null;
  return (
    <Dialog open={!!preset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add {preset.label}</DialogTitle>
          <DialogDescription>Provider config — fields vary by kind. Validate with Test after saving.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={preset.label} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{preset.kind === "oidc" ? "Issuer / discovery URL" : "Metadata URL"}</Label>
            <Input value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="https://acme.okta.com" />
          </div>
          {preset.kind === "oidc" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Client ID</Label>
                <Input value={clientId} onChange={(e) => setClientId(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Client secret</Label>
                <Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Group claim (optional)</Label>
            <Input value={groupClaim} onChange={(e) => setGroupClaim(e.target.value)} placeholder="smplify_role" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!name || !issuer || create.isPending} onClick={async () => {
            await create.mutateAsync({
              kind: preset.kind, name,
              issuer: preset.kind === "oidc" ? issuer : undefined,
              metadataUrl: preset.kind === "saml" ? issuer : undefined,
              clientId: clientId || undefined,
              groupClaim: groupClaim || undefined,
              enabled: true,
            });
            onClose();
          }}>{create.isPending ? "Adding…" : "Add provider"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
